// SPDX-License-Identifier: GPL-3.0-only
// Adapted from OpenSaga (https://github.com/opensagadev/saga), GPL version 3:
// move.cpp:5092 LightSabreComboCode; fighting.cpp:272 ComboHitFrame;
// gameobjects.cpp:4620-4738; utilities.cpp:662 SphereSphereOverlapScaleY.
// This is an unpowered grounded Jedi timing/collision subset, not the engine.
// Caller owns animation blending, movement, enemy eligibility, blocking,
// immunity, damage effects, airborne actions, power-ups and level scripting.

function finite(value, name) {
  if (!Number.isFinite(value)) throw new Error(`Invalid ${name}.`);
  return value;
}

function vector(value, name) {
  if (!value || !['x', 'y', 'z'].every(axis => Number.isFinite(value[axis]))) throw new Error(`Invalid ${name}.`);
  return value;
}

export function sphereOverlapScaleY(a, radiusA, yRadiusA, b, radiusB, yRadiusB) {
  vector(a, 'first position'); vector(b, 'second position');
  for (const radius of [radiusA, yRadiusA, radiusB, yRadiusB]) {
    if (!Number.isFinite(radius) || radius <= 0) throw new Error('Collision radii must be positive.');
  }
  const radius = radiusA + radiusB;
  let dy = b.y - a.y;
  if (radiusA !== yRadiusA || radiusB !== yRadiusB) dy *= radius / (yRadiusA + yRadiusB);
  return (b.x - a.x) ** 2 + dy ** 2 + (b.z - a.z) ** 2 <= radius ** 2;
}

// Heading is radians, zero toward native +Z. Position is collision center,
// not model feet. The original damage mode deliberately replaces locators
// with these forward samples; this is not a large radial damage shortcut.
export function sampledSaberTarget(attacker, targets) {
  vector(attacker.position, 'attacker position'); finite(attacker.heading, 'heading');
  if (!Array.isArray(targets)) throw new Error('Targets must be an array.');
  const direction = { x: Math.sin(attacker.heading), z: Math.cos(attacker.heading) };
  const points = [0.2, 0.5, 0.35].map(distance => ({
    x: attacker.position.x + direction.x * distance, y: attacker.position.y,
    z: attacker.position.z + direction.z * distance,
  }));
  let nearest = null, nearestDistance = 1e6;
  for (const target of targets) {
    if (target === attacker || target.eligible === false || target.dead) continue;
    vector(target.position, 'target position');
    const radius = target.radius, yRadius = target.yRadius ?? radius;
    if (!points.some(point => sphereOverlapScaleY(target.position, radius, yRadius, point, 0.075, 0.075))) continue;
    const distance = (target.position.x - attacker.position.x) ** 2 +
      (target.position.y - attacker.position.y) ** 2 + (target.position.z - attacker.position.z) ** 2;
    if (distance < nearestDistance) { nearest = target; nearestDistance = distance; }
  }
  // CannotKill/immunity is evaluated after nearest selection in the source:
  // an immune foreground target must not let the strike pass to one behind it.
  return nearest;
}

const SUFFIXES = ['_1', '_2a', '_2b', '_3a', '_3b', '_3c', '_3d'];

export class JediCombo {
  constructor(config, { clipEndFrame, onStrike = () => {} } = {}) {
    if (!(config?.actions instanceof Map) || typeof clipEndFrame !== 'function' || typeof onStrike !== 'function') throw new Error('Invalid combo configuration.');
    this.config = config; this.clipEndFrame = clipEndFrame; this.onStrike = onStrike;
    this.alternate = 0;
    this.reset();
  }

  reset() {
    this.active = false; this.waiting = false; this.stage = 0; this.branch = 0;
    this.frame = 1; this.remainingWindow = 0; this.latched = false;
    this.perfect = true; this.hit = false; this.animation = null; this.family = 1;
  }

  get state() {
    return { active: this.active, waiting: this.waiting, action: this.animation?.action ?? null,
      clip: this.animation?.clip ?? null, frame: this.frame, stage: this.stage, branch: this.branch,
      family: this.family, remainingWindow: this.remainingWindow, latched: this.latched, perfect: this.perfect };
  }

  available(family, branch) { return this.config.actions.has(`combo${family}${SUFFIXES[branch]}`); }

  startClip(branch) {
    const action = `combo${this.family}${SUFFIXES[branch]}`;
    const animation = this.config.actions.get(action);
    if (!animation) { this.reset(); return false; }
    const endFrame = this.clipEndFrame(animation.clip);
    if (!Number.isFinite(endFrame) || endFrame <= 1) throw new Error(`Invalid original clip duration: ${animation.clip}`);
    this.branch = branch; this.animation = animation; this.endFrame = endFrame;
    this.frame = 1; this.hit = false; this.waiting = false; this.latched = false; this.perfect = true;
    this.remainingWindow = 0; this.active = true;
    return true;
  }

  next() {
    const previous = this.branch;
    this.stage++;
    let normal, perfect;
    if (this.stage === 1) { normal = 1; perfect = 2; }
    else if (previous === 1) { normal = 3; perfect = 4; }
    else { normal = 5; perfect = 6; }
    return this.startClip(this.perfect && this.available(this.family, perfect) ? perfect : normal);
  }

  press() {
    if (!this.active) {
      this.family = this.available(2, 0) && this.alternate === 0 ? 2 : 1;
      this.alternate = this.family === 2 ? 1 : 0;
      this.stage = 0;
      return this.startClip(0);
    }
    if (this.waiting && this.stage < 2) return this.next();
    if (this.stage >= 2 || this.latched) return false;
    if ((this.endFrame - this.frame) / this.animation.playbackRate < 0.1) this.latched = true;
    else this.perfect = false;
    return this.latched;
  }

  strike() {
    if (this.hit) return;
    this.hit = true;
    const damage = this.stage === 2 ? (this.branch === 6 ? 3 : 1) : (this.branch === 2 ? 2 : 1);
    this.onStrike({ ...this.state, damage });
  }

  update(dt) {
    finite(dt, 'combo timestep');
    if (dt < 0 || dt > 60) throw new Error('Combo timestep must be between 0 and 60 seconds.');
    // Consume time across clip/window boundaries so a slow frame cannot
    // silently skip a strike or extend the source's input windows.
    let remaining = dt;
    for (let steps = 0; this.active && steps < 8; steps++) {
      if (this.waiting) {
        if (this.latched && this.stage < 2) { this.next(); continue; }
        this.remainingWindow -= remaining;
        if (this.remainingWindow <= 0) this.reset();
        break;
      }
      const animation = this.animation;
      const terminal = this.stage === 2 ? Math.max(1, this.endFrame - animation.blendOut * animation.playbackRate) : this.endFrame;
      const seconds = Math.max(0, (terminal - this.frame) / animation.playbackRate);
      const elapsed = Math.min(remaining, seconds);
      this.frame = Math.min(terminal, this.frame + elapsed * animation.playbackRate);
      remaining -= elapsed;
      const hitFrame = animation.eventFrames[0], endEvent = animation.eventFrames[1];
      if (!this.hit && hitFrame > 0 && this.frame >= hitFrame) this.strike();
      if (this.stage === 2 && !this.hit && hitFrame === 0 && endEvent > 0 && this.frame >= endEvent) this.strike();
      if (elapsed < seconds) break;
      if (!this.hit) this.strike(); // Original end-of-action fallback.
      if (this.stage === 2) { this.reset(); break; }
      this.waiting = true; this.remainingWindow = 0.2;
      if (remaining === 0 && !this.latched) break;
    }
    return this.state;
  }
}
